import type { EstimateLineItem, EstimateMembershipOffer } from '@/types/estimate';
import type { EstimateDocTotalsOverride } from '@/components/documents/estimates/blocks/EstimateDocumentRenderer';
import { membershipOfferTaxTotal } from '@/lib/sales/membership-offer';

/**
 * The AUTHORITATIVE totals for an accepted-membership design/single-scope PDF, so
 * a signed document reconciles — list subtotal, membership savings, a separate
 * plan-price line, member tax, and a member grand total (work + subscription) —
 * instead of re-summing the list-priced projection.
 *
 * The SINGLE source shared by the public quote and the client portal so the two
 * signed documents can never drift (the exact parity gap the review bot flags).
 * Returns `undefined` when membership is not being accepted, matching the design
 * PDF's "no override → derive from the estimate" contract.
 */
export function buildMembershipTotalsOverride(args: {
  /** The frozen offer being accepted; `null`/absent → no override. */
  membershipOffer: EstimateMembershipOffer | null | undefined;
  /** List-priced subtotal (the projection the checklist is priced from). */
  listSubtotal: number;
  /** Non-membership discount + its label. */
  discountTotal: number;
  discountLabel: string;
  /** Member-adjusted deposit due. */
  deposit: number;
  /** Selected (list-priced) lines + all estimate lines, for the member tax calc. */
  selectedLines: readonly EstimateLineItem[];
  allLines: readonly EstimateLineItem[];
  /** Tax to fall back to when the offer can't produce a member tax total. */
  fallbackTaxTotal: number;
  /**
   * What the customer ALREADY saves on these lines through the plan(s) they
   * hold. An existing member accepting another plan's offer keeps it: the
   * offer's `savings` is the increment on top, and the signed document must
   * show the same cumulative row the hosted quote shows. Absent/0 for a
   * non-member.
   */
  heldMembershipSavings?: number;
  /** Names the plans the savings come from ("Gold + Platinum"). */
  membershipSavingsLabel?: string;
}): EstimateDocTotalsOverride | undefined {
  const offer = args.membershipOffer;
  if (!offer) return undefined;
  const membershipSavings =
    Math.round(((args.heldMembershipSavings ?? 0) + (offer.savings ?? 0) + Number.EPSILON) * 100) /
    100;

  const memberTax = membershipOfferTaxTotal(offer, args.selectedLines, args.allLines);
  // Total due is the member work total PLUS the membership subscription price —
  // the same figure the approval endpoint charges (mirrors the hosted quote).
  const grandTotal =
    Math.round(
      (offer.memberGrandTotal + (offer.planPrice ?? 0) + (offer.setupFee ?? 0) + Number.EPSILON) *
        100,
    ) / 100;

  return {
    subtotal: args.listSubtotal,
    membershipSavings,
    membershipPlanPrice: offer.planPrice ?? 0,
    membershipSetupFee: offer.setupFee ?? 0,
    membershipPlanLabel: offer.planName ?? 'Membership',
    ...(args.membershipSavingsLabel ? { membershipSavingsLabel: args.membershipSavingsLabel } : {}),
    discountTotal: args.discountTotal,
    discountLabel: args.discountLabel,
    taxBreakdown: [],
    taxTotal: memberTax ?? args.fallbackTaxTotal,
    grandTotal,
    depositAmount: args.deposit,
  };
}
